import { z } from 'zod';
import { SeveritySchema } from '../domain/finding.js';
import { IdSchema, IsoDateTimeSchema } from './common.js';
/**
 * Vulnerability intelligence for a finding: what the authoritative sources say about it.
 *
 * Three layers, never mixed (ADR 0006):
 * - **Source facts** — NVD, the CVE's CNA, CISA KEV, FIRST EPSS, MITRE CWE, OSV advisories, and
 *   Guardian's curated rule assessments. Each carries its source and when it was fetched.
 * - **Derived, deterministically** — the priority, from fixed rules over those facts.
 * - **AI-derived** — summaries and recommendations from the agent, stored separately and labelled
 *   as such; never written over a fact (phase K2).
 */
export const CVE_ID = /^CVE-\d{4}-\d{4,7}$/;
export const CveIdSchema = z.string().regex(CVE_ID);
export const KnowledgeSourceSchema = z.enum([
    'NVD',
    'CISA_KEV',
    'FIRST_EPSS',
    'MITRE_CWE',
    'OSV',
    'GUARDIAN_RULES',
    'ZAP',
]);
export const ProvenanceSchema = z.object({
    source: KnowledgeSourceSchema,
    /** When the platform fetched it from the source. Absent for facts that ship with the platform. */
    fetchedAt: IsoDateTimeSchema.optional(),
    /** A version or URL identifying exactly what was read. */
    reference: z.string().max(500).optional(),
});
/** A software component a finding names, e.g. a JavaScript library and version the scanner saw. */
export const SoftwareComponentSchema = z.object({
    ecosystem: z.enum(['npm']),
    name: z.string().min(1).max(214),
    version: z.string().min(1).max(64),
});
/** What the scanner said the finding is, kept from the report. Facts from the scanner, not inferred. */
export const FindingClassificationSchema = z.object({
    pluginId: z.string().max(20).optional(),
    cweId: z.number().int().positive().optional(),
    wascId: z.number().int().positive().optional(),
    cveIds: z.array(CveIdSchema).max(50).default([]),
    component: SoftwareComponentSchema.optional(),
});
export const CvssScoreSchema = z.object({
    version: z.enum(['2.0', '3.0', '3.1', '4.0']),
    vector: z.string().max(300),
    baseScore: z.number().min(0).max(10),
    severity: z.enum(['NONE', 'LOW', 'MEDIUM', 'HIGH', 'CRITICAL']),
    /** Who assigned it: `nvd@nist.gov`, the CNA's address, or `guardian` for a rule assessment. */
    assigner: z.string().max(200).optional(),
    /** NVD's `Primary` (its own) or `Secondary` (the CNA's). */
    type: z.enum(['Primary', 'Secondary']).optional(),
});
export const WeaknessSchema = z.object({
    cweId: z.number().int().positive(),
    name: z.string().max(500),
    abstraction: z.string().max(40).optional(),
    /** MITRE's own mapping (view 1344), not ours. */
    owaspTop10: z.object({ id: z.string().max(20), name: z.string().max(200) }).optional(),
    provenance: ProvenanceSchema,
});
export const CveIntelSchema = z.object({
    cveId: CveIdSchema,
    /** LOADED — in the knowledge base. PENDING — requested, not fetched yet. NOT_FOUND — NVD has no such record. */
    status: z.enum(['LOADED', 'PENDING', 'NOT_FOUND']),
    description: z.string().max(4000).optional(),
    published: z.string().max(40).optional(),
    lastModified: z.string().max(40).optional(),
    vulnStatus: z.string().max(60).optional(),
    cvss: z.array(CvssScoreSchema),
    cweIds: z.array(z.string().max(30)),
    epss: z
        .object({
        score: z.number(),
        percentile: z.number(),
        date: z.string().max(20),
        provenance: ProvenanceSchema,
    })
        .optional(),
    kev: z
        .object({
        vendor: z.string().max(200),
        product: z.string().max(200),
        dateAdded: z.string().max(20),
        dueDate: z.string().max(20).optional(),
        knownRansomware: z.string().max(40).optional(),
        requiredAction: z.string().max(1000).optional(),
        provenance: ProvenanceSchema,
    })
        .optional(),
    /** Fixed versions from OSV advisories for the component the finding names. */
    fixedIn: z.array(z.object({
        ecosystem: z.string().max(40),
        package: z.string().max(214),
        fixed: z.string().max(64),
        advisoryId: z.string().max(100),
    })),
    references: z
        .array(z.object({ url: z.string().max(2048), tags: z.array(z.string().max(60)) }))
        .max(30),
    provenance: ProvenanceSchema.optional(),
});
/**
 * The score the finding is shown with, and where it came from.
 * `CVE` — the highest published CVSS among its CVEs. `RULE_ASSESSMENT` — Guardian's curated vector
 * for the scanner rule. `SCANNER_ONLY` — neither exists; only the scanner's own risk is shown.
 */
export const IntelSeveritySchema = z.object({
    basis: z.enum(['CVE', 'RULE_ASSESSMENT', 'SCANNER_ONLY']),
    cvss: CvssScoreSchema.optional(),
    cveId: CveIdSchema.optional(),
    rationale: z.string().max(1000).optional(),
    rulesVersion: z.string().max(40).optional(),
    reviewed: z.object({ by: z.string(), at: z.string() }).nullable(),
    scannerRisk: SeveritySchema,
    provenance: ProvenanceSchema,
});
export const PriorityLevelSchema = z.enum(['ACT_NOW', 'HIGH', 'MEDIUM', 'LOW', 'INFORMATIONAL']);
export const PrioritySchema = z.object({
    level: PriorityLevelSchema,
    reasons: z.array(z.string().max(300)),
});
/** GET /v1/engagements/{id}/findings/{findingId}/intel */
export const VulnerabilityIntelSchema = z.object({
    findingId: IdSchema,
    classification: FindingClassificationSchema,
    weakness: WeaknessSchema.nullable(),
    severity: IntelSeveritySchema,
    cves: z.array(CveIntelSchema),
    priority: PrioritySchema,
    /** UNAVAILABLE when the knowledge base is not deployed or not loaded yet; the facts are then partial. */
    knowledgeBase: z.enum(['READY', 'UNAVAILABLE']),
    generatedAt: IsoDateTimeSchema,
});
/** GET /v1/knowledge/status */
export const KnowledgeStatusSchema = z.object({
    state: z.enum(['READY', 'EMPTY', 'UNAVAILABLE']),
    counts: z.object({
        cves: z.number().int(),
        kev: z.number().int(),
        cwes: z.number().int(),
        advisories: z.number().int(),
    }),
    lastCatalogSync: IsoDateTimeSchema.nullable(),
});
//# sourceMappingURL=vulnerability-intel.js.map